import { useState, useEffect, useMemo } from 'react';
import { GameState } from '../types';
import { isBidValid } from '../utils/playerUtils';

/**
 * Custom hook for managing bid defaults based on game state
 */
export const useBidDefaults = (
  gameState: GameState | null,
  myPlayerId: string | null
) => {
  const [bidQuantity, setBidQuantity] = useState(1);
  const [bidFaceValue, setBidFaceValue] = useState(0); // 0 means no face value selected

  const isMyTurn = useMemo(() => {
    if (!gameState || !myPlayerId) return false;
    return gameState.players[gameState.currentPlayerIndex]?.id === myPlayerId;
  }, [gameState, myPlayerId]);

  useEffect(() => {
    if (!gameState) return;

    if (gameState.currentBid && isMyTurn) {
      // Default to same quantity as current bid, no face value pre-selected
      setBidQuantity(gameState.currentBid.quantity);
      setBidFaceValue(0); // Don't pre-select face value
    } else if (!gameState.currentBid) {
      // Reset to quantity 1, no face value pre-selected
      setBidQuantity(1);
      setBidFaceValue(0);
    }
  }, [gameState?.currentBid, isMyTurn, gameState]);

  const canBid = useMemo(() => {
    // Can't bid without selecting a face value
    if (bidFaceValue === 0) return false;
    return isBidValid(bidQuantity, bidFaceValue, gameState?.currentBid || null);
  }, [bidQuantity, bidFaceValue, gameState?.currentBid]);

  return {
    bidQuantity,
    setBidQuantity,
    bidFaceValue,
    setBidFaceValue,
    canBid,
    isMyTurn,
  };
};

/**
 * Custom hook for game state helpers
 */
export const useGameHelpers = (gameState: GameState | null) => {
  const canStartGame = useMemo(() => {
    return gameState?.phase === 'waiting' && (gameState?.players.length ?? 0) >= 2;
  }, [gameState?.phase, gameState?.players.length]);

  const isPlaying = useMemo(() => {
    return gameState?.phase === 'playing';
  }, [gameState?.phase]);

  const isWaiting = useMemo(() => {
    return gameState?.phase === 'waiting';
  }, [gameState?.phase]);

  const isGameOver = useMemo(() => {
    return gameState?.phase === 'game_over';
  }, [gameState?.phase]);

  return {
    canStartGame,
    isPlaying,
    isWaiting,
    isGameOver,
  };
};

/**
 * Custom hook for auto-clearing challenge results
 * NOTE: Does not clear during revealing phase - players must ready up
 */
export const useAutoClearChallenge = (
  challengeResult: any,
  clearChallengeResult: () => void,
  gamePhase: string | undefined,
  delay: number = 5000
) => {
  useEffect(() => {
    // Don't auto-clear during revealing phase - players need to ready up
    if (challengeResult && gamePhase !== 'revealing') {
      const timer = setTimeout(() => {
        clearChallengeResult();
      }, delay);
      return () => clearTimeout(timer);
    }
  }, [challengeResult, clearChallengeResult, gamePhase, delay]);
};